<script setup lang="ts">
/**
 * Right cell for the mobile service types table.
 *
 * Renders the price right-aligned in monospace:
 *   - positive  → green, "18.00 $"
 *   - zero      → muted, "0.00 $"
 *   - undefined → very faint "–" (no price set)
 *
 * Price is received as the raw major-unit number from the API (no minor-unit
 * conversion needed); formatMoney is called with precision: 0, fractionDigits: 2.
 */
import { computed } from 'vue'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'

const props = defineProps<{
  price?: number | null
  width?: string
}>()

const hasPrice = computed(
  () => props.price !== undefined && props.price !== null && !Number.isNaN(props.price),
)

const formatted = computed(() => {
  if (!hasPrice.value) return null
  const { formatted: str } = formatMoney({ amount: props.price!, precision: 0, fractionDigits: 2 })
  return `${str} ${currencySymbol}`
})

const priceClass = computed(() => {
  if (!hasPrice.value) return 'price--none'
  return props.price === 0 ? 'price--zero' : 'price--positive'
})
</script>

<template>
  <td class="st-mobile-right font-tufte">
    <span :class="['price', priceClass]">{{ formatted ?? '–' }}</span>
  </td>
</template>

<style scoped>
.st-mobile-right {
  vertical-align: middle;
  text-align: right;
}

.price {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-base);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.price--positive {
  color: var(--color-money-positive);
}
.price--zero {
  color: var(--color-tufte-muted);
}
.price--none {
  color: color-mix(in srgb, var(--color-tufte-muted) 50%, var(--color-tufte-bg));
}
</style>
